import { useEffect, useRef, useState } from "react";
import { Logo } from "@/shared/components/ui/Logo";
import { cn } from "@/shared/lib/utils";

interface AuthLoadingOverlayProps {
  /** `true` while the session resolves; flip to `false` to fade out and unmount. */
  show: boolean;
  /**
   * Minimum time the overlay stays visible before its exit may begin, so an
   * instant resolve still plays the full fade rather than flashing for a
   * frame. Should be ≥ `fadeMs`.
   */
  minDisplayMs?: number;
  /**
   * Fade duration in ms. MUST match the `duration-300` CSS transition below;
   * only override alongside a matching class change. Used here for the
   * `transitionend` fallback timer (jsdom / reduced motion don't emit it).
   */
  fadeMs?: number;
  /** Fires exactly once after the overlay has fully faded out and unmounted. */
  onExited?: () => void;
}

type Phase = "entering" | "visible" | "exiting" | "gone";

/**
 * Full-screen auth loading splash: an opaque page-canvas overlay (`z-modal`)
 * with a pulsing brand wordmark. Per design.md §14 it fades in, honors a
 * minimum display time, and always completes its fade-OUT before unmounting —
 * no abrupt cut even when auth resolves instantly. Driven by a CSS
 * `transition-opacity` (§12-compliant, ≤300ms) plus the small state machine
 * below; no Framer Motion.
 *
 * The guard keeps a single instance of this component mounted across
 * loading→authed (rendered outside the branch), so the overlay can fade out
 * over the already-mounted app and reveal it without a swap. See RequireAuth.
 */
export function AuthLoadingOverlay({
  show,
  minDisplayMs = 450,
  fadeMs = 300,
  onExited,
}: AuthLoadingOverlayProps) {
  const [phase, setPhase] = useState<Phase>(show ? "entering" : "gone");
  // Drives the opacity utility: false → opacity-0, true → opacity-100.
  const [opaque, setOpaque] = useState(false);
  const [minDone, setMinDone] = useState(false);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;
  const initialShow = useRef(show);
  const minDisplayRef = useRef(minDisplayMs);

  // Mount: if shown, trigger the entrance fade on the next frame (so the
  // browser paints opacity-0 first) and start the minimum-display timer.
  useEffect(() => {
    if (!initialShow.current) return;
    const raf = requestAnimationFrame(() => {
      if (phaseRef.current === "entering") {
        setOpaque(true);
        setPhase("visible");
      }
    });
    const minT = setTimeout(() => setMinDone(true), minDisplayRef.current);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(minT);
    };
    // Mount-only: initial `show` / `minDisplayMs` are captured in refs above.
  }, []);

  // Begin the exit once the session resolved AND the minimum display elapsed;
  // re-show (re-auth mid-exit) cancels a pending exit and returns to visible.
  useEffect(() => {
    if (show) {
      if (phase === "exiting") {
        clearTimeout(exitTimer.current);
        setOpaque(true);
        setPhase("visible");
      }
      return;
    }
    if (minDone && (phase === "visible" || phase === "entering")) {
      setPhase("exiting");
      setOpaque(false);
      // Fallback for environments that don't emit `transitionend`.
      exitTimer.current = setTimeout(() => setPhase("gone"), fadeMs + 60);
    }
  }, [show, minDone, phase, fadeMs]);

  // Fire onExited once, when the overlay reaches the gone state.
  useEffect(() => {
    if (phase === "gone") onExitedRef.current?.();
  }, [phase]);

  useEffect(() => () => clearTimeout(exitTimer.current), []);

  if (phase === "gone") return null;

  return (
    <div
      data-testid="auth-loading-overlay"
      role="status"
      aria-label="Loading"
      onTransitionEnd={(e) => {
        // Opacity is the only property this element transitions; tolerate a
        // missing propertyName (jsdom doesn't populate it) but stay strict in
        // real browsers so a stray bubbled transition can't end us early.
        if (phase === "exiting" && (!e.propertyName || e.propertyName === "opacity")) {
          clearTimeout(exitTimer.current);
          setPhase("gone");
        }
      }}
      className={cn(
        "fixed inset-0 z-modal flex items-center justify-center bg-surface",
        "transition-opacity duration-300",
        opaque ? "opacity-100" : "opacity-0",
      )}
    >
      <div className="animate-pulse">
        <Logo presentation="static" className="h-10" />
      </div>
    </div>
  );
}
